import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ErreurApi, api } from '../lib/apiClient';
import { enregistrerFichier } from '../lib/fichiers';
import { lienGmailRelance } from '../lib/relance';
import { corpsAttestation, destinatairesAttestation, sujetAttestation } from '../lib/attestation';
import { gmailPret, useCompteGmail } from '../hooks/useCompteGmail';
import { CompteGmail } from './CompteGmail';
import { Modale } from './Elements';
import { PiedFormulaire } from '../pages/ReferentielsPages';
import type { PersonneDetail } from '../types';

/**
 * L'ATTESTATION DE PAIEMENT depuis la fiche (2026-10-31, demande de
 * Georges) : on confirme a qui l'envoyer — l'adresse de la fiche, celles
 * des contacts —, puis le message s'ouvre dans Gmail.
 *
 * DEUX CHEMINS (2026-11-02). Avec l'acces Gmail accorde a la connexion
 * Google (permission « Envoi d'email »), le serveur pose un BROUILLON
 * dans la boite de l'utilisateur, PDF attache, et l'ecran l'ouvre :
 * c'est le chemin normal. Sans acces, le PDF est
 * enregistre sur le poste et un message Gmail prerempli s'ouvre, dans
 * lequel on glisse le fichier — un lien de composition ne porte pas de
 * piece jointe, constate le 2026-10-31.
 *
 * « Voir le PDF » l'ouvre dans un onglet, sans rien envoyer : on relit
 * avant d'expedier.
 */
export function FormulaireAttestation({
  ouverte,
  personne,
  anneeId,
  anneeLibelle,
  onFermer,
}: {
  ouverte: boolean;
  personne: PersonneDetail;
  anneeId: string;
  anneeLibelle: string;
  onFermer: () => void;
}) {
  const { session, entiteActive, peut } = useAuth();
  const [choisis, setChoisis] = useState<Set<string> | null>(null);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [ouvertureEnCours, setOuvertureEnCours] = useState(false);
  const gmail = useCompteGmail(ouverte);
  const permissionBrouillon = peut('message.brouillon');
  const parBrouillon = permissionBrouillon && gmailPret(gmail.donnees);

  const destinataires = destinatairesAttestation(personne);

  // Meme garde que les autres modales de la fiche : les cases se posent a
  // l'ouverture depuis la donnee deja a l'ecran, et se reposent a chaque
  // reouverture.
  const cle = ouverte ? `${personne.id}:${anneeId}` : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setChoisis(new Set(destinataires.filter((d) => d.parDefaut).map((d) => d.email)));
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const chemin = `/api/personnes/${personne.id}/attestation-paiement.pdf?anneeId=${encodeURIComponent(anneeId)}`;

  const voir = async () => {
    setErreur(null);
    setOuvertureEnCours(true);
    try {
      const { contenu } = await api.telecharger(chemin);
      const url = URL.createObjectURL(contenu);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Production impossible.');
    } finally {
      setOuvertureEnCours(false);
    }
  };

  /** Le chemin normal : un brouillon avec la piece jointe, ouvert dans Gmail. */
  const brouillon = async (emails: string[]) => {
    const { url } = await api.post<{ url: string }>(`/api/personnes/${personne.id}/attestation-paiement/brouillon`, {
      anneeId,
      destinataires: emails,
    });
    window.open(url, '_blank', 'noopener');
  };

  /** Le repli : le PDF sur le poste, et un message prerempli a completer. */
  const telechargementEtLien = async (emails: string[]) => {
    const { contenu, nomFichier } = await api.telecharger(chemin);
    enregistrerFichier(contenu, nomFichier ?? `attestation-paiement-${anneeLibelle}.pdf`);
    const ecole = entiteActive?.code
      ? entiteActive.code.charAt(0) + entiteActive.code.slice(1).toLowerCase()
      : 'Artistic';
    window.open(
      lienGmailRelance(
        session?.utilisateur.email ?? '',
        emails,
        sujetAttestation(ecole, personne.prenom, personne.nom, anneeLibelle),
        corpsAttestation(entiteActive?.raisonSociale ?? 'l’association', personne.prenom, personne.nom, anneeLibelle),
      ),
      '_blank',
      'noopener',
    );
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    const emails = destinataires.filter((d) => choisis?.has(d.email)).map((d) => d.email);
    if (emails.length === 0) {
      setErreur('Choisissez au moins un destinataire.');
      return;
    }
    setEnCours(true);
    try {
      if (parBrouillon) await brouillon(emails);
      else await telechargementEtLien(emails);
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Production impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const basculer = (email: string) => {
    setChoisis((courant) => {
      const suivant = new Set(courant ?? []);
      if (suivant.has(email)) suivant.delete(email);
      else suivant.add(email);
      return suivant;
    });
  };

  return (
    <Modale titre="Attestation de paiement" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <p className="text-sm text-encre-600">
          Attestation de {personne.prenom} {personne.nom.toUpperCase()} pour l’année {anneeLibelle} : le montant
          dû, les règlements reçus (chèques à encaisser compris) et le reste à payer.{' '}
          {parBrouillon
            ? 'Un brouillon avec le PDF en pièce jointe sera posé dans votre boîte Gmail et s’ouvrira, prêt à relire et à envoyer.'
            : 'Le PDF sera enregistré sur votre poste et un message Gmail s’ouvrira, prêt à l’envoyer : glissez-y le fichier en pièce jointe.'}
        </p>

        <fieldset>
          <legend className="etiquette">Envoyer à</legend>
          {destinataires.length === 0 ? (
            <p className="text-sm text-encre-500">
              Aucune adresse email sur la fiche ni sur ses contacts : le message ne pourra pas s’ouvrir.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {destinataires.map((d) => (
                <li key={d.email}>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={choisis?.has(d.email) ?? false}
                      onChange={() => basculer(d.email)}
                    />
                    <span>
                      <span className="text-encre-700">{d.libelle}</span>
                      <span className="ml-2 text-encre-500">{d.email}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <div className="rounded-md border border-encre-100 bg-encre-50/50 p-3">
          <CompteGmail etat={gmail.donnees} recharger={gmail.recharger} permission={permissionBrouillon} compact />
        </div>

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          libelle={parBrouillon ? 'Créer le brouillon Gmail' : 'Enregistrer et ouvrir Gmail'}
          enfants={
            <div className="flex justify-start">
              <button type="button" className="bouton-secondaire" onClick={voir} disabled={ouvertureEnCours}>
                {ouvertureEnCours ? 'Production…' : 'Voir le PDF'}
              </button>
            </div>
          }
        />
      </form>
    </Modale>
  );
}
